import { currentSRPSchemaVersion } from './constants';
import Version from './Version.js';
import { srpdParentLevelTemplate, srpTemplate, type SRPSchemaInterface } from '../models/SRPSchema';
import API from '../APIs/API.js';
import { AddedClusters } from '../models/ImportProcessData';
import { SelectOption } from './dropDownOptions';
import { srpErrorsTemplate } from '../models/SRPErrors';


export async function searchKeyPerson({
    api,
    inputValue
}: {
    api: API,
    inputValue: string
}) {
    console.log(`Search Key Person: ${inputValue}`);
    if(inputValue.trim() === ""){
        return [];
    }

    if(inputValue.length > 50){
        inputValue = inputValue.substring(0, 50);
    }

   try {
        const response = await api.fetch(`/srp/form/users/search?searchTerm=${inputValue}`);

        if (!response.ok) {
            throw new Error(`Response status: ${response.status}, statusText: ${response.statusText}`);
        }
        const searchResults = await response.json();

        if (!Array.isArray(searchResults)) {
            throw new Error("Unexpected response format");
        }
        return searchResults.map((item) => ({
            label: item,
            value: item
        }));
   } catch (error: any) {
        console.error("Error searching key person:", error.message);
        return [];
   }

}

export function populateMachineListErrorsSection(machineList: any, setMachineListErrors: any) {
    const newMachineList = machineList;
    console.log("Populating Machine List Errors Section with Machine List: ", newMachineList);

    setMachineListErrors((prevState: any) => {
        // To do: what should be done if the user came back from edit page, need a failsafe to make sure they can't submit without filling the form
        //  Should form validation be triggered?
        const newErrorList = [];
        let machineIndex = 0;
        for (const machine of newMachineList){
            newErrorList.push({
                variantListErrors: []
            });
            // newErrorList[machineIndex].variantListErrors = [];
            for (const variant of machine.variantList) {
                const variantError = {
                    clusterListErrors: [],
                } as any;
                for (const cluster of variant.clusterList) {
                    const clusterErrors = {
                        selectionCriteriaListErrors: [],
                    } as any

                    for (const _ of cluster.selectionCriteriaList) {
                        const selectionCriteriaErrors = {
                        };
                        clusterErrors.selectionCriteriaListErrors.push(selectionCriteriaErrors);
                    }
                    
                    variantError.clusterListErrors.push(clusterErrors);
                }
                newErrorList[machineIndex].variantListErrors.push(variantError);
            }
            machineIndex++;
        }

        console.log("Machine List Errors: ", newErrorList);

        return newErrorList;
    });
}


export function populateSupplierInVariantList(machineList: any[]) {
    for (const machine of machineList) {
        for (const variant of machine.variantList) {
            variant.supplier = "";
        }
    }
}




export function getItemByValueInDropdownOptions(options: SelectOption[], valueToFind: string) {
    try {
        for (const option of options) {
            if (option.value === valueToFind) {
                return option;
            }
        }

        if (!valueToFind) {
            return null;
        }

        return {value: valueToFind, label: `${valueToFind}`};

    } catch (error) {
        console.error(`getItemByValueInDropdownOptions:`, error);
        return {value: valueToFind, label: `${valueToFind}`};
    }
    
}

export function getItemsByValuesInDropdownOptions(options: SelectOption[], valuesToFind: string[]) {
    try {
        const result = [];

        for (const valueToFind of valuesToFind) {
            for (const option of options) {
                if (option.value === valueToFind) {
                    result.push(option);
                    break;
                }
            }
        }
        
        return result.length > 0 ? result : valuesToFind.map((value) => ({value: value, label: `${value}`}));
    } catch (error) {
        console.error(`getItemsByValuesInDropdownOptions:`, error);
        return valuesToFind?.map((value) => ({value: value, label: `${value}`})) ?? [];
    }
}

export function isSigningAllowedForModule(allowedSigningModules: string[], moduleName: string): boolean {
    if (!Array.isArray(allowedSigningModules) || typeof moduleName !== 'string') {
        return false;
    }

    const normalizedModuleName = moduleName.trim().toLowerCase();
    if (!normalizedModuleName) {
        return false;
    }

    return allowedSigningModules.some((allowedModule) => {
        return typeof allowedModule === 'string' && allowedModule.trim().toLowerCase() === normalizedModuleName;
    });
}

export const getClusterURLs = (jsonData: SRPSchemaInterface) => {

    let clusterURLs = [];

    let machineIndex = 0;
    for (const machine of jsonData.machineList) {
        let variantIndex = 0;
        for (const variant of machine.variantList) {
            let clusterIndex = 0;
            for (const cluster of variant.clusterList) {
                clusterURLs.push({
                    clusterType: cluster.clusterType,
                    machineType: machine.machineType,
                    clusterUrl: cluster.clusterUrl,
                    updateType: cluster.updateType,
                    machineIndex: machineIndex,
                    variantIndex: variantIndex,
                    clusterIndex: clusterIndex
                });
                clusterIndex++;
            }
            variantIndex++;
        }
        machineIndex++;
    }

    return clusterURLs;
}

// Helper function to fetch cluster version data
async function fetchClusterVersionData(clusterName: string, api: API) {
    const params = new URLSearchParams({ clusterName });
    console.log("Fetching cluster version for:", clusterName);
    return await api.fetch(`/srp/form/clusters/versions/latest?${params}`);
}

// Helper function to process version data response
async function processVersionResponse(versionData: Response, clusterName: string) {
    const assumeNew = (versionData.status === 404);

    if (!versionData.ok && !assumeNew) {
        console.error("Error fetching cluster version:", versionData.statusText);
        throw new Error(`Failed to fetch cluster version: ${versionData.statusText}`);
    }

    if (assumeNew) {
        console.log("Assuming new cluster version for:", clusterName);
        return { version: "0.1.0", assumeNew: true };
    }

    const data = await versionData.json();
    console.log("Fetched cluster version data:", data);
    return { version: data as string, assumeNew: false };
}

// Helper function to create and validate version object
function createAndValidateVersion(returnedVersion: string) {
    const version = new Version(returnedVersion);
    console.log("Current Version: ", version.version);

    if (!version || version === null) {
        console.error("Invalid version data received:", returnedVersion);
        throw new Error("Invalid version data received");
    }

    return version;
}

// Helper function to update cluster with version information
function updateClusterVersion(cluster: { clusterName: string; clusterVersion: string; changeLevel?: string; }, version: Version, assumeNew: boolean) {
    const compareResult = version.compare("1.0.0");
    console.log("Current version", version.version, " compared to 1.0.0: ", compareResult);
    
    if (compareResult && compareResult > 0) {
        version.version = "1.0.0";
    }

    cluster.clusterVersion = version.version;
    if (assumeNew) {
        cluster.changeLevel = "MAJOR"; // set to "major" so it will be 1.0.0 when new, and can't be submitted if changed
    }

    console.log("Updated Version: ", version.version);
}

export async function handleFNV23Versioning(currentClusterList : {
    clusterName: string;
    clusterVersion: string;
    changeLevel?: string;
}[],
    addedClusters: any[],
    api: API

) {
    console.log("Handling FNV2.3 Versioning for clusters...", currentClusterList);
    
    for (let cluster of currentClusterList) {
        // find the same cluster in addedClusters
        const matchingAddedCluster = addedClusters.find((addedCluster) => addedCluster.label === cluster.clusterName);
        if (matchingAddedCluster?.isImported) {
            console.log(`Skipping imported cluster: ${cluster.clusterName}`);
            continue;
        }
        const versionData = await fetchClusterVersionData(cluster.clusterName, api);
        const { version: returnedVersion, assumeNew } = await processVersionResponse(versionData, cluster.clusterName);
        const version = createAndValidateVersion(returnedVersion);
        updateClusterVersion(cluster, version, assumeNew);
    }
}


export async function handleFNV23VersioningForMachineList({
    machineList,
    addClusters,
    api
}: {
    machineList: any[],
    addClusters?: AddedClusters[],
    api: API
}) {
    console.log("Handling FNV2.3 Versioning for clusters...", machineList);

    for (let i = 0; i < machineList.length; i++) {
        const machine = machineList[i];
        for (let j = 0; j < machine.variantList.length; j++) {
            const variant = machine.variantList[j];
            for (const cluster of variant.clusterList) {
                // find the same cluster in addClusters
                if (addClusters) {
                    const matchingAddedCluster = addClusters.find((addedCluster) => addedCluster.clusterName === cluster.clusterName && addedCluster.machineIndex === i && addedCluster.variantIndex === j);
                    if (matchingAddedCluster) {
                        console.log(`Skipping imported cluster: ${cluster.clusterName}`);
                        continue;
                    }
                }

                const versionData = await fetchClusterVersionData(cluster.clusterName, api);
                const { version: returnedVersion, assumeNew } = await processVersionResponse(versionData, cluster.clusterName);
                const version = createAndValidateVersion(returnedVersion);
                updateClusterVersion(cluster, version, assumeNew);
            }
        }
    }

}

export const getJsonContent = (
    {
    textFieldValues,
    architectureType,
    selectedKeyPersonOption,
    selectedResponsibleOptions,
    moduleInformation,
    machineList,
    preassemblyImage
}: any
) => {
    return {
        srpdName: textFieldValues.srpdName,
        srpdVersion: textFieldValues.srpdVersion,
        srpAssignee: selectedKeyPersonOption ? selectedKeyPersonOption.value : "",
        srpWatchers: selectedResponsibleOptions.map((option: any) => option.value),
        srpSchemaVersion: currentSRPSchemaVersion,
        electricalArchitecture: architectureType ? [architectureType] : [],
        moduleInformation: moduleInformation,
        machineList: machineList,
        preassemblyImage: preassemblyImage
    };
  };


  export async function prefillSRPWatchers({api, textFields, srpWatchers, setSelectedResponsibleOptions, setKeyContactsIsLoading}){
    setKeyContactsIsLoading(true);

    let keyPersonOptions = [];
    for(const watcher of srpWatchers){
       let inputValue = watcher.trim();

        const searchResults = await searchKeyPerson({api,inputValue});

        if(searchResults.length > 0){
            keyPersonOptions.push(searchResults[0]);
            textFields.srpWatchers.push(searchResults[0].value);
        }
    }

    setSelectedResponsibleOptions(keyPersonOptions);
    setKeyContactsIsLoading(false);
}


export async function prefillSRPAssignee({api, textFields, srpAssignee, setSelectedKeyPersonOption}){
    let inputValue = srpAssignee.trim();
    const searchResults = await searchKeyPerson({
        api,
        inputValue
    });
    setSelectedKeyPersonOption(searchResults.length > 0 ? searchResults[0] : null);
    textFields.srpAssignee = searchResults.length > 0 ? searchResults[0].value : "";
}


const ALLOWED_HOSTNAMES = [
  'github.com',
  'raw.githubusercontent.com',
  'ford.atlassian.net',
  'ford-sandbox-640.atlassian.net',
];

// Helper to check if hostname is allowed or a valid subdomain
function isAllowedHostname(hostname: string): boolean {
  return ALLOWED_HOSTNAMES.some(allowed => {
    return (
      hostname === allowed ||
      (hostname.endsWith('.' + allowed) && hostname.length > allowed.length + 1)
    );
  });
}

// Create utility functions
export const isValidUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    // Only allow https URLs from trusted domains
    return parsedUrl.protocol === 'https:' && 
           isAllowedHostname(parsedUrl.hostname)
  } catch (e) {
    console.error("Invalid URL:", url, e);
    return false;
  }
};

export const sanitizeUrl = (url: string): string => {
  if (!isValidUrl(url)) {
    return '#'; // Return safe default
  }
  return url;
};


export const getJiraAPIBaseHost = (env: string): "ford.atlassian.net" | "ford-sandbox-640.atlassian.net" => {
     if (env === 'production') {
        return 'ford.atlassian.net'
    }

    return 'ford-sandbox-640.atlassian.net';
}


export const resetSRPFormState = ({
      setProjectKey,
      setModuleName,
      setMachineList,
      setModuleInformation,
      setPreassemblyImage,
      setPreassemblyImageErrors,
      setSelectedKeyPersonOption,
      setSelectedResponsibleOptions,
      setTextFieldValues,
      setTextFieldErrors,
      setMachineListErrors,
      setModuleInformationErrors,
      
    }) => {
      setProjectKey("");
      setModuleName("");
      setMachineList(structuredClone(srpTemplate.machineList));
      setModuleInformation(structuredClone(srpTemplate.moduleInformation));
      setPreassemblyImage(structuredClone(srpTemplate.preassemblyImage));
      setPreassemblyImageErrors(structuredClone(srpErrorsTemplate.preassemblyImageErrors));
      setSelectedKeyPersonOption(undefined);
      setSelectedResponsibleOptions([]);
      setTextFieldValues(structuredClone(srpdParentLevelTemplate));
      setTextFieldErrors(structuredClone(srpErrorsTemplate.textFieldErrors));
      setMachineListErrors(structuredClone(srpErrorsTemplate.machineListErrors));
      setModuleInformationErrors(structuredClone(srpErrorsTemplate.moduleInformationErrors));
    }


export function sanitizePublicUrl(url: string) {
    // Only allow empty string or relative path, starting with '/'
    if (typeof url !== 'string') return '';
    if (!url || url === '') return '';
    if (RegExp(/^\/[a-zA-Z0-9_/.-]*$/).exec(url)) {
        return url;
    }
    // Otherwise, fallback to '' (public root)
    return '';
}